/* sugar:assets/faq.js@2.0.0 */
/**
 * FAQ section — category tabs, the sliding/underline indicator and the
 * expand/collapse-all controls.
 *
 * Category tabs are not authored in Liquid: each FAQ Category block carries its
 * label and icon as data-* attributes on its panel, and the nav is built from
 * those. That keeps one source of truth and lets the editor add, remove or
 * re-label a category without a section reload.
 *
 * One controller per [data-faq] root, so the section ships no inline script.
 */

(function () {
  function root(sectionId) {
    return document.querySelector('[data-faq][data-section-id="' + sectionId + '"]');
  }

  function Faq(el) {
    this.el = el;
    this.cfg = JSON.parse(el.getAttribute('data-faq') || '{}');
    this.nav = el.querySelector('[data-faq-nav]');
    this.navList = el.querySelector('[data-faq-nav-list]');
    this.slidingBg = el.querySelector('[data-faq-sliding-bg]');
    this.active = 0;
    this.bind();
    this.build();
    this.setCategory(0, { reset: false });
    this.scheduleMeasure();
  }

  Faq.prototype.panels = function () {
    return [].slice.call(this.el.querySelectorAll('.sugar-faq__panel'));
  };

  Faq.prototype.group = function (panel) {
    return panel ? panel.querySelector('[data-accordion-group]') : null;
  };

  Faq.prototype.activePanel = function () {
    return this.panels()[this.active] || null;
  };

  Faq.prototype.bind = function () {
    var self = this;

    this.navList.addEventListener('click', function (e) {
      var tab = e.target.closest('[role="tab"]');
      if (!tab) return;
      var tabs = [].slice.call(self.navList.querySelectorAll('[role="tab"]'));
      var i = tabs.indexOf(tab);
      if (i !== -1) self.setCategory(i);
    });

    this.navList.addEventListener('keydown', function (e) {
      var count = self.panels().length;
      if (!count) return;
      var next = self.active;
      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          next = (self.active - 1 + count) % count;
          break;
        case 'ArrowRight':
        case 'ArrowDown':
          next = (self.active + 1) % count;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = count - 1;
          break;
        default:
          return;
      }
      e.preventDefault();
      self.setCategory(next);
      var tabs = self.navList.querySelectorAll('[role="tab"]');
      if (tabs[next]) tabs[next].focus();
    });

    var expand = this.el.querySelector('[data-faq-expand-all]');
    var collapse = this.el.querySelector('[data-faq-collapse-all]');
    if (expand) {
      expand.addEventListener('click', function () {
        var group = self.group(self.activePanel());
        if (group) window.SugarInteractive.accordion.setAll(group, true);
      });
    }
    if (collapse) {
      collapse.addEventListener('click', function () {
        var group = self.group(self.activePanel());
        if (group) window.SugarInteractive.accordion.setAll(group, false);
      });
    }

    this.el.addEventListener('sugar:accordion:toggle', function () {
      self.syncControls();
    });

    window.addEventListener('resize', function () {
      self.measure();
    });
    window.addEventListener('load', function () {
      self.measure();
    }, { once: true });
  };

  Faq.prototype.build = function () {
    var panels = this.panels();
    var self = this;

    // Drop previously built tabs, keeping the sliding-background span.
    [].slice.call(this.navList.querySelectorAll('li')).forEach(function (li) {
      li.remove();
    });

    panels.forEach(function (panel, index) {
      panel.dataset.index = index;
      var id = panel.id.replace('faq-panel-', '');
      var d = panel.dataset;

      var li = document.createElement('li');
      li.setAttribute('role', 'presentation');

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'sugar-faq__nav-tab';
      btn.setAttribute('role', 'tab');
      btn.id = 'faq-tab-' + id;
      btn.setAttribute('aria-controls', 'faq-panel-' + id);

      var icon = self.buildIcon(panel, d);
      if (icon) btn.appendChild(icon);

      var text = document.createElement('span');
      text.className = 'sugar-faq__nav-text';
      text.textContent = d.categoryName || 'Category';
      btn.appendChild(text);

      if (self.cfg.showFaqCount) {
        var count = document.createElement('span');
        count.className = 'sugar-faq__nav-count';
        count.textContent = '(' + panel.querySelectorAll('.sugar-faq-item').length + ')';
        btn.appendChild(count);
      }

      li.appendChild(btn);
      self.navList.appendChild(li);
    });

    this.nav.hidden = panels.length <= 1;
    if (this.active >= panels.length) this.active = Math.max(0, panels.length - 1);
  };

  Faq.prototype.buildIcon = function (panel, d) {
    var wrap = document.createElement('span');
    wrap.className = 'sugar-faq__nav-icon';

    if (d.customIcon) {
      wrap.appendChild(this.img(d.customIcon));
      return wrap;
    }
    var tpl = panel.querySelector(':scope > template[data-faq-nav-icon]');
    if (tpl && tpl.content.firstElementChild) {
      wrap.appendChild(tpl.content.cloneNode(true));
      return wrap;
    }
    return null;
  };

  Faq.prototype.img = function (src) {
    var img = document.createElement('img');
    img.src = src;
    img.alt = '';
    img.loading = 'lazy';
    return img;
  };

  Faq.prototype.setCategory = function (index, opts) {
    opts = opts || {};
    var panels = this.panels();
    if (!panels.length) return;
    index = Math.max(0, Math.min(index, panels.length - 1));
    this.active = index;

    var tabs = this.navList.querySelectorAll('[role="tab"]');
    for (var i = 0; i < tabs.length; i++) {
      var on = i === index;
      tabs[i].classList.toggle('is-active', on);
      tabs[i].setAttribute('aria-selected', on ? 'true' : 'false');
      tabs[i].tabIndex = on ? 0 : -1;
    }

    var self = this;
    panels.forEach(function (panel, i) {
      panel.classList.toggle('is-active', i === index);
      // Switching categories resets the accordion, matching the previous behavior.
      if (opts.reset !== false) {
        var group = self.group(panel);
        if (group) window.SugarInteractive.accordion.setAll(group, false);
      }
    });

    if (this.cfg.openFirstItem) this.openFirst();
    this.syncControls();
    this.scheduleMeasure();
  };

  Faq.prototype.openFirst = function () {
    var panel = this.activePanel();
    var first = panel && panel.querySelector('[data-accordion]');
    if (first) window.SugarInteractive.accordion.open(first, false);
  };

  Faq.prototype.syncControls = function () {
    var group = this.group(this.activePanel());
    var expand = this.el.querySelector('[data-faq-expand-all]');
    var collapse = this.el.querySelector('[data-faq-collapse-all]');
    if (!group) return;

    var allOpen = window.SugarInteractive.accordion.allOpen(group);
    var allClosed = window.SugarInteractive.accordion.allClosed(group);
    if (expand) {
      expand.classList.toggle('is-inactive', allOpen);
      expand.disabled = allOpen;
      expand.setAttribute('aria-pressed', allOpen ? 'true' : 'false');
    }
    if (collapse) {
      collapse.classList.toggle('is-inactive', allClosed);
      collapse.disabled = allClosed;
      collapse.setAttribute('aria-pressed', allClosed ? 'true' : 'false');
    }
  };

  // Indicator geometry depends on final text metrics, so re-measure as late
  // work lands: next frame, after icons decode, after webfonts swap.
  Faq.prototype.scheduleMeasure = function () {
    var self = this;
    requestAnimationFrame(function () {
      self.measure();
      requestAnimationFrame(function () { self.measure(); });
      setTimeout(function () { self.measure(); }, 120);
    });

    [].slice.call(this.navList.querySelectorAll('img')).forEach(function (img) {
      if (!img.complete) img.addEventListener('load', function () { self.measure(); }, { once: true });
    });

    if (typeof ResizeObserver !== 'undefined') {
      if (this._ro) this._ro.disconnect();
      this._ro = new ResizeObserver(function () { self.measure(); });
      this._ro.observe(this.navList);
    }

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () { self.measure(); }).catch(function () {});
    }
  };

  Faq.prototype.measure = function () {
    var tabs = this.navList.querySelectorAll('.sugar-faq__nav-tab');
    var tab = tabs[this.active];
    if (!tab) return;

    // The indicator's geometry only exists once it has been measured. The stylesheet
    // draws a per-tab fallback until this lands, so the strip is never unmarked.
    this.navList.setAttribute('data-faq-measured', '');

    if (this.cfg.underline) {
      var left = tab.offsetLeft;
      var width = tab.offsetWidth;
      if (this.cfg.underlineText) {
        var textEl = tab.querySelector('.sugar-faq__nav-text');
        if (textEl) {
          left += (tab.offsetWidth - textEl.offsetWidth) / 2;
          width = textEl.offsetWidth;
        }
      }
      this.navList.style.setProperty('--underline-top', tab.offsetTop + tab.offsetHeight - 2 + 'px');
      this.navList.style.setProperty('--underline-left', left + 'px');
      this.navList.style.setProperty('--underline-width', width + 'px');
    }

    if (this.cfg.slidingBg && this.slidingBg) {
      this.slidingBg.style.setProperty('--sliding-bg-top', tab.offsetTop + 'px');
      this.slidingBg.style.setProperty('--sliding-bg-left', tab.offsetLeft + 'px');
      this.slidingBg.style.setProperty('--sliding-bg-height', tab.offsetHeight + 'px');
      this.slidingBg.style.setProperty('--sliding-bg-width', tab.offsetWidth + 'px');
    }
  };

  function instance(el) {
    if (!el) return null;
    if (!el._sugarFaq) el._sugarFaq = new Faq(el);
    return el._sugarFaq;
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-faq]').forEach(instance);
  }

  function rebuild(sectionId) {
    var faq = instance(root(sectionId));
    if (!faq) return;
    faq.build();
    faq.setCategory(Math.min(faq.active, faq.panels().length - 1), { reset: false });
  }

  window.SugarReady(function () { initAll(); });

  document.addEventListener('shopify:section:load', function (e) {
    var el = root(e.detail.sectionId);
    if (!el) return;
    el._sugarFaq = null;
    instance(el);
  });

  document.addEventListener('shopify:section:reorder', function (e) { rebuild(e.detail.sectionId); });
  document.addEventListener('shopify:block:add', function (e) { rebuild(e.detail.sectionId); });
  document.addEventListener('shopify:block:remove', function (e) { rebuild(e.detail.sectionId); });

  // Selecting a block in the editor reveals the category that holds it.
  document.addEventListener('shopify:block:select', function (e) {
    var faq = instance(root(e.detail.sectionId));
    if (!faq) return;
    faq.panels().forEach(function (panel, index) {
      if (panel.contains(e.target) || panel.id === 'faq-panel-' + e.detail.blockId) {
        faq.setCategory(index, { reset: false });
      }
    });
  });

  // Editing a category's label or icon changes only its panel's data-* attributes,
  // which no section event reports.
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('DOMContentLoaded', function () {
      document.querySelectorAll('[data-faq]').forEach(function (el) {
        new MutationObserver(function () { rebuild(el.dataset.sectionId); }).observe(el, {
          attributes: true,
          attributeFilter: ['data-category-name', 'data-category-icon', 'data-custom-icon'],
          subtree: true
        });
      });
    });
  }
})();
